import React, { useEffect, useRef, useState } from 'react'
import { CloseOutlined, RobotOutlined, SendOutlined, UserOutlined } from '@ant-design/icons'
import { Avatar, Button, Input } from 'antd'
import c from 'classnames'
import style from './ai-chatbot.module.sass'

import { useLlm } from './useLlm'
import { FormLlmInterfacesQuery } from './__gen'
import { LlmChatMessageSenderType } from '@/__gen/types'

const { TextArea } = Input


type AiChatbotProps = {
	onClose: () => void
	formLlmInterface: NonNullable<FormLlmInterfacesQuery['formLlmInterface_List']>['objects'][number]
}

export const AiChatbot: React.FC<AiChatbotProps> = ({ onClose, formLlmInterface }) => {

	const { send, messages, loading } = useLlm({formLlmInterface})
	
	const [inputValue, setInputValue] = useState('')
	const messagesEndRef = useRef<HTMLDivElement>(null)

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	const onSend = () => {
		send({content: inputValue})
		setInputValue('')
	}

	useEffect(() => {
		scrollToBottom()
	}, [messages])

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			onSend()
		}
	}

	const formatTime = (date: Date) => {
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
	}

	return (
		<div className={style.aiChatbotPanel}>
			<div className={style.chatHeader}>
				<div className={style.headerContent}>
					<RobotOutlined className={style.headerIcon} />
					<span className={style.headerTitle}>AI Support</span>
				</div>
				<Button type="text" icon={<CloseOutlined />} onClick={onClose} className={style.closeButton} />
			</div>

			<div className={style.chatContainer}>
				<div className={style.messagesContainer}>
					{messages && messages.map((message) => (
						<div
							key={message.id}
							className={c(style.messageRow, {
								[style.userMessage]: message.sender == LlmChatMessageSenderType.User,
								[style.aiMessage]: message.sender == LlmChatMessageSenderType.Agent,
							})}
						>
							<Avatar
								className={style.avatar}
								icon={message.sender == LlmChatMessageSenderType.User ? <UserOutlined /> : <RobotOutlined />}
								style={{
									backgroundColor: message.sender == LlmChatMessageSenderType.User ? '#1890ff' : '#52c41a',
								}}
								size="small"
							/>
							<div className={style.messageContent}>
								<div className={style.messageText}>{message.content}</div>
								<div className={style.messageTime}>{formatTime(new Date(message.created))}</div>
							</div>
						</div>
					))}
					{loading && (
						<div className={c(style.messageRow, style.aiMessage)}>
							<Avatar
								className={style.avatar}
								icon={<RobotOutlined />}
								style={{ backgroundColor: '#52c41a' }}
								size="small"
							/>
							<div className={style.messageContent}>
								<div className={style.typingIndicator}>
									<span></span>
									<span></span>
									<span></span>
								</div>
							</div>
						</div>
					)}
					<div ref={messagesEndRef} />
				</div>

				<div className={style.inputContainer}>
					<TextArea
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyPress={handleKeyPress}
						placeholder="Ask me anything about this form..."
						autoSize={{ minRows: 1, maxRows: 2 }}
						className={style.messageInput}
						disabled={loading}
						autoFocus={true}
					/>
					<Button
						type="primary"
						icon={<SendOutlined />}
						onClick={onSend}
						disabled={!inputValue.trim() || loading}
						className={style.sendButton}
						size="small"
					/>
				</div>
			</div>
		</div>
	)
}
